import { Temporal } from "temporal-polyfill";
import { z } from "zod";
import { DomainError } from "./errors.js";
export const SCHEDULED_SEND_EXECUTOR_POLL_MS = 5_000;
export const SCHEDULED_SEND_PRESENCE_QUIET_MS = 3_000;
export const SCHEDULED_SEND_PRE_INVOKE_DELAY_CAP_MS = 5_000;
export const SCHEDULED_SEND_ADMISSION_BUDGET_MS = SCHEDULED_SEND_EXECUTOR_POLL_MS
    + SCHEDULED_SEND_PRESENCE_QUIET_MS
    + SCHEDULED_SEND_PRE_INVOKE_DELAY_CAP_MS;
export const SCHEDULED_SEND_CATCH_UP_MS = 15 * 60_000;
const LocalDateTimeSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{3})?)?$/u, "Use an exact local date and time to the minute, second, or millisecond.");
const UtcOffsetSchema = z.string().regex(/^[+-](?:0\d|1[0-4]):[0-5]\d$/u, "Use an exact UTC offset such as +08:00.");
const ScheduledSendShapeSchema = z.object({
    // WHY [BOL-SCHEDULE-WORK-BINDING-001]: the approval hash must freeze the
    // exact canonical Work owner, not merely trust a mutable proposal column.
    // Falsifier: moving an approved schedule to another task leaves it executable.
    workItemId: z.uuid(),
    // The authenticated API ingress instant is proof metadata, not model input.
    // Legacy/non-chat schedules may omit it; an Ops-chat relative schedule must
    // persist it inside the immutable proposal effect used to derive executeAt.
    askedAt: z.iso.datetime().optional(),
    localDateTime: LocalDateTimeSchema,
    timeZone: z.string().trim().min(1).max(80),
    utcOffset: UtcOffsetSchema,
    executeAt: z.iso.datetime(),
    catchUpUntil: z.iso.datetime()
}).strict();
/**
 * WHY [BOL-SCHEDULE-TUPLE-001]: a future approval authorizes one wall time,
 * IANA rule, offset, UTC instant, Work item, and bounded catch-up deadline.
 * Reconstruct the tuple under current tzdata wherever it crosses a process
 * boundary. A changed rule or hand-edited instant fails closed; nothing may
 * silently shift, rewrite, or extend the approved schedule.
 */
export const ScheduledSendSchema = ScheduledSendShapeSchema.superRefine((schedule, context) => {
    const reason = scheduledSendTupleReason(schedule);
    if (reason)
        context.addIssue({ code: "custom", message: reason });
});
export function resolveScheduledSend(rawInput, now = new Date()) {
    const input = z.object({
        workItemId: z.uuid(),
        askedAt: z.iso.datetime().optional(),
        localDateTime: LocalDateTimeSchema,
        timeZone: z.string().trim().min(1).max(80),
        utcOffset: UtcOffsetSchema.optional()
    }).strict().parse(rawInput);
    const plain = parsePlainDateTime(input.localDateTime);
    const precision = scheduledSendPrecision(input.localDateTime);
    const choices = possibleScheduledSendChoices(plain, input.timeZone, precision);
    if (choices.length === 0) {
        throw new DomainError("SCHEDULE_TIME_DOES_NOT_EXIST", "That local time does not exist in the selected timezone. Choose another time.", { localDateTime: input.localDateTime, timeZone: input.timeZone });
    }
    if (choices.length > 1 && input.utcOffset === undefined) {
        throw new DomainError("SCHEDULE_TIME_AMBIGUOUS", "That local time occurs twice. Choose the exact UTC offset shown for the intended time.", { localDateTime: input.localDateTime, timeZone: input.timeZone, choices });
    }
    const chosen = input.utcOffset === undefined
        ? choices[0]
        : choices.find((choice) => choice.utcOffset === input.utcOffset);
    if (!chosen) {
        throw new DomainError("SCHEDULE_OFFSET_MISMATCH", "That UTC offset does not identify the selected local time in this timezone.", { localDateTime: input.localDateTime, timeZone: input.timeZone, choices });
    }
    assertScheduledSendApprovalWindow(chosen.executeAt, now);
    return ScheduledSendSchema.parse({
        workItemId: input.workItemId,
        ...(input.askedAt === undefined ? {} : { askedAt: input.askedAt }),
        localDateTime: input.localDateTime,
        timeZone: input.timeZone,
        utcOffset: chosen.utcOffset,
        executeAt: chosen.executeAt,
        catchUpUntil: new Date(Date.parse(chosen.executeAt) + SCHEDULED_SEND_CATCH_UP_MS).toISOString()
    });
}
export function assertScheduledSendApprovalWindow(scheduleOrExecuteAt, now = new Date()) {
    const executeAt = typeof scheduleOrExecuteAt === "string"
        ? scheduleOrExecuteAt
        : ScheduledSendSchema.parse(scheduleOrExecuteAt).executeAt;
    const delay = Date.parse(executeAt) - now.getTime();
    // WHY [BOL-SUBMINUTE-APPROVAL-WINDOW-002]: approval is send authority only
    // while the executor still owns its complete normal admission budget B.
    // Falsifier: authority commits after T-B, or B drifts from the connector's
    // shared poll, quiet-observation, and bounded pre-invocation delays.
    if (!Number.isFinite(delay) || delay < SCHEDULED_SEND_ADMISSION_BUDGET_MS) {
        throw new DomainError("SCHEDULE_TOO_SOON", "There is not enough time left to approve and send this safely. Choose a later time.");
    }
    // WHY [BOL-SCHEDULE-GENERAL-HORIZON-001]: a future action's requested time
    // is business intent, not a demo-window policy. PostgreSQL can retain the
    // inert job until its exact T-B, while edit/cancel and the final freshness
    // checks keep long-lived authority revocable and fail-closed. A fixed
    // 30-day horizon rejected valid preapproved work without closing a safety
    // boundary. Falsifier: a canonical future instant is rejected only because
    // it is far away, or removing this cap bypasses the unchanged T-B check.
}
export function scheduledSendAdmissionAt(scheduleOrExecuteAt) {
    const executeAt = typeof scheduleOrExecuteAt === "string"
        ? scheduleOrExecuteAt
        : ScheduledSendSchema.parse(scheduleOrExecuteAt).executeAt;
    const milliseconds = Date.parse(executeAt);
    if (!Number.isFinite(milliseconds)) {
        throw new DomainError("INVALID_SCHEDULE_TIME", "The scheduled send time is invalid.");
    }
    return new Date(milliseconds - SCHEDULED_SEND_ADMISSION_BUDGET_MS).toISOString();
}
export function scheduledSendExecutionReason(input) {
    const parsed = ScheduledSendSchema.safeParse(input.schedule);
    if (!parsed.success)
        return "scheduled_tuple_invalid";
    const schedule = parsed.data;
    if (canonicalInstant(input.availableAt) !== scheduledSendAdmissionAt(schedule))
        return "scheduled_job_time_changed";
    if (canonicalInstant(input.receiptExpiresAt) !== schedule.catchUpUntil)
        return "scheduled_receipt_expiry_changed";
    const opensAt = input.phase === "ADMISSION" ? scheduledSendAdmissionAt(schedule) : schedule.executeAt;
    if (input.now.getTime() < Date.parse(opensAt)) {
        return input.phase === "ADMISSION" ? "scheduled_not_admitted" : "scheduled_not_due";
    }
    if (input.now.getTime() >= Date.parse(schedule.catchUpUntil))
        return "scheduled_catch_up_expired";
    return null;
}
function scheduledSendTupleReason(schedule) {
    if (schedule.askedAt !== undefined) {
        if (canonicalInstant(schedule.askedAt) !== schedule.askedAt) {
            return "The authenticated ask time must use canonical millisecond UTC form.";
        }
        if (Date.parse(schedule.askedAt) >= Date.parse(schedule.executeAt)) {
            return "The authenticated ask time must precede the scheduled instant.";
        }
    }
    let plain;
    try {
        plain = parsePlainDateTime(schedule.localDateTime);
    }
    catch {
        return "The scheduled local date and time is invalid.";
    }
    let choices;
    try {
        choices = possibleScheduledSendChoices(plain, schedule.timeZone, scheduledSendPrecision(schedule.localDateTime));
    }
    catch {
        return "The scheduled timezone is invalid.";
    }
    const chosen = choices.find((choice) => choice.utcOffset === schedule.utcOffset);
    if (!chosen || chosen.executeAt !== canonicalInstant(schedule.executeAt)) {
        return "The scheduled timezone, offset, and UTC instant no longer identify the same time.";
    }
    if (canonicalInstant(schedule.executeAt) !== schedule.executeAt) {
        return "The scheduled UTC instant must use canonical millisecond UTC form.";
    }
    const catchUpUntil = new Date(Date.parse(schedule.executeAt) + SCHEDULED_SEND_CATCH_UP_MS).toISOString();
    if (canonicalInstant(schedule.catchUpUntil) !== schedule.catchUpUntil || schedule.catchUpUntil !== catchUpUntil) {
        return "The scheduled catch-up deadline must be exactly 15 minutes after the approved instant.";
    }
    return null;
}
function possibleScheduledSendChoices(plain, timeZone, precision) {
    const fields = {
        year: plain.year,
        month: plain.month,
        day: plain.day,
        hour: plain.hour,
        minute: plain.minute,
        second: plain.second,
        millisecond: plain.millisecond,
        timeZone
    };
    const earlier = Temporal.ZonedDateTime.from(fields, { disambiguation: "earlier" });
    const later = Temporal.ZonedDateTime.from(fields, { disambiguation: "later" });
    const target = plain.toString({ smallestUnit: precision });
    const byOffset = new Map();
    for (const candidate of [earlier, later]) {
        if (candidate.toPlainDateTime().toString({ smallestUnit: precision }) !== target)
            continue;
        byOffset.set(candidate.offset, {
            utcOffset: candidate.offset,
            executeAt: new Date(Number(candidate.epochMilliseconds)).toISOString()
        });
    }
    return [...byOffset.values()].sort((left, right) => left.executeAt.localeCompare(right.executeAt));
}
function parsePlainDateTime(value) {
    const plain = Temporal.PlainDateTime.from(value);
    const precision = scheduledSendPrecision(value);
    if (plain.microsecond !== 0 || plain.nanosecond !== 0
        || (precision === "millisecond" && plain.millisecond === 0)
        || plain.toString({ smallestUnit: precision }) !== value) {
        throw new DomainError("INVALID_SCHEDULE_LOCAL_TIME", "Use one canonical local date and time to the minute, second, or nonzero millisecond.");
    }
    return plain;
}
function scheduledSendPrecision(value) {
    return value.length === 16 ? "minute" : value.length === 19 ? "second" : "millisecond";
}
function canonicalInstant(value) {
    if (value === null)
        return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}
//# sourceMappingURL=scheduled-send.js.map